import { clsx } from "clsx";
import { isEmpty, isEqual, pick } from "lodash";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Autosuggest from "react-autosuggest";
import { CSSTransition } from "react-transition-group";
import { Icon, RenderWhen } from "../../../components";
import DatePickerModal from "../../../components/DatePickerModal";
import useKeyboardShortcut from "../../../components/hooks/useKeyboardShortcut";
import {
  FavoriteQuery,
  useFavoriteQueries,
  useGetLatestQueries
} from "../../../hooks";
import useToggleClass from "../../../hooks/useToggleClass";
import { getURLGridQueryParams } from "../../../utils";
import ErrorIcon from "../../header/search/ErrorIcon";
import GlobalSearchButtons from "../../header/search/GlobalSearchButtons";
import { NtiLink } from "../../samples/components/topheader/components";
import {
  applyDatePickerFn,
  GridConfig,
  historyAndFavoritesClickFn,
  onBulkSearchFn,
  onSearchFn,
  Selections
} from "../@types";
import { SEARCH_QUERY_LIMIT } from "../consts";
import { getShowNtiLink } from "../containers/utils";
import useSearchModals, {
  SEARCH_MODALS_NAMES,
  SearchModalsNamesType
} from "../hooks/useSearchModals";
import { removeLabelFromSearchSelections } from "../util";
import SearchAutosuggest from "./SearchAutosuggest";
import QueryBuilderContainer from "./autosuggest/QueryBuilderContainer";
import SearchModals from "./modals/SearchModals";
import { getNtiValueFromQuery, refangUrlInText } from "./utils";
import { DEFAULT_QUERY } from "../hooks";
import { ResetSearchContext } from "../../header/context";

const ENTERED_KEYS = Object.freeze({
  ENTER: "Enter",
  BACKSPACE: "Backspace"
});

const MENU_MORE_VALUE = "-expand-section-trigger-";
const INPUT_CONTAINER_ID = "global-search-container";

interface Props {
  id: string;
  errorMsg?: string | null;
  isLoading: boolean;
  placeholder?: string;
  onChangeInput: (value: string) => void;
  onSearch: (params: onSearchFn) => void;
  onBulkSearch: (params: onBulkSearchFn) => void;
  onApplyDatePicker: (date: applyDatePickerFn) => void;
  searchValue: string;
  setSearchBarExpanded?: (expanded: boolean) => void;
  staticSearchBar?: boolean;
  searchLocation?: string;
  selections?: Selections;
  onSuggestionSearch?: (value: onSearchFn) => void;
  onHistoryAndFavoritesSearch?: (value: historyAndFavoritesClickFn) => void;
  className?: string;
  transformedQuery?: string;
  gridConfig?: GridConfig;
}

const SearchInput = (props: Props) => {
  const {
    id,
    errorMsg,
    isLoading,
    placeholder = "Search for file hash, URL, domain, IP address, malware family, malware type, actor and more...",
    onChangeInput,
    onSearch,
    onBulkSearch,
    searchValue = "",
    onApplyDatePicker,
    setSearchBarExpanded,
    staticSearchBar = false,
    searchLocation,
    selections,
    onSuggestionSearch,
    onHistoryAndFavoritesSearch,
    className,
    transformedQuery = "",
    gridConfig
  } = props;

  const [inProp, setInProp] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isDatePicker, setIsDatePicker] = useState(false);
  const [value, setValue] = useState("");
  const [selectedFavourite, setSelectedFavourite] = useState<
    FavoriteQuery | undefined
  >(undefined);
  const { resetSearch } = useContext(ResetSearchContext);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { modalName, modalOpened, openModal, closeModal } = useSearchModals();
  const { data: { data: favourites } = {} } = useFavoriteQueries();
  const { data: { data: searchHistory } = {} } = useGetLatestQueries();

  useKeyboardShortcut("s", { altKey: true }, () => {
    if (!gridConfig?.query_builder) inputRef.current?.focus();
  });

  const selectionsWithoutLabel = removeLabelFromSearchSelections(selections);

  const isSearchValueWithinLimit = searchValue.length <= SEARCH_QUERY_LIMIT;
  const isSearchQuery = Boolean(searchValue) || !isEmpty(selections);

  useToggleClass(
    INPUT_CONTAINER_ID,
    isActive && !staticSearchBar,
    "search__container--expanded"
  );
  const setSearchBarActive = (searchBarIsActive: boolean) => {
    setIsActive(searchBarIsActive);
    setSearchBarExpanded?.(searchBarIsActive);
  };

  const handleChange = (
    e: React.FormEvent<HTMLInputElement>,
    { newValue, method }: Autosuggest.ChangeEvent
  ) => {
    if (newValue === MENU_MORE_VALUE || method !== "type") return;

    setValue(newValue);
    onChangeInput(newValue);
  };

  const handleQueryBuilderInputChange = (newValue: string) => {
    setValue(newValue);
    onChangeInput(newValue);
  };

  const handleSearch = () => {
    setSearchBarActive(false);
    const transformedValue = refangUrlInText(value);
    onSearch({ query: transformedValue });
  };

  // reset search grid when search navigation is clicked
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setValue(DEFAULT_QUERY);
    onSearch({ query: DEFAULT_QUERY });
  }, [resetSearch]);

  const handleQueryBuilderSearch = (newValue: string) => {
    setSearchBarActive(false);
    setValue(newValue);
    onSearch({ query: newValue });
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = event => {
    // focus on end of input instead of at beginning
    const { value: focusValue } = event?.currentTarget;
    event.currentTarget.value = "";
    event.currentTarget.value = focusValue;
    setSearchBarActive(true);
    setInProp(true);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = event => {
    // if the focus is on the search container, don't close the search bar
    // this is to prevent the search bar from not responding to the click event on buttons
    if (
      event.relatedTarget &&
      (event.relatedTarget as Element).closest(".search__container")
    )
      return;
    setSearchBarActive(false);
  };

  const handleQueryBuilderFocus: React.FocusEventHandler<
    HTMLInputElement
  > = () => {
    setSearchBarActive(true);
    setInProp(true);
  };

  const handleKeyPress: React.KeyboardEventHandler<HTMLElement> = event => {
    const { key } = event || {};
    const canSearch =
      key === ENTERED_KEYS.ENTER &&
      searchValue[searchValue.length - 1] !== ":" &&
      !isLoading;

    if (canSearch) {
      handleSearch();
    } else if (!isActive) {
      setSearchBarActive(true);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = event => {
    // onKeyPress won't capture backspace and onKeyDown is trigered on list navigation as well as on search input changes
    // part of the issue is in react autosuggest component - workaround is to handle only backspace in onKeyDown event
    const { key } = event || {};

    if (key === "Escape") {
      setSearchBarActive(false);
      inputRef.current?.blur();
    }

    if (key === ENTERED_KEYS.BACKSPACE && !isActive) {
      setSearchBarActive(true);
    }
  };

  const handleButtonClick = ({
    type,
    favourite
  }: {
    type: SearchModalsNamesType;
    favourite?: FavoriteQuery;
  }) => {
    setSearchBarActive(false);
    if (
      [
        SEARCH_MODALS_NAMES.SHARE_QUERY,
        SEARCH_MODALS_NAMES.ADD_FAVOURITE
      ].includes(modalName ?? "") &&
      !isSearchValueWithinLimit
    ) {
      return;
    }
    if (type === SEARCH_MODALS_NAMES.SEARCH) {
      handleSearch();
      return;
    }

    openModal(type);
    setSelectedFavourite(favourite);
  };

  const showDatepicker = (val: boolean) => {
    setIsDatePicker(val);
  };

  const inputProps: Autosuggest.InputProps<unknown> & {
    "data-testid": string;
  } = {
    id,
    ref: inputRef,
    className: "search__input",
    "data-testid": "search_page_input",
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyPress: handleKeyPress,
    onKeyDown: handleKeyDown,
    value: value || searchValue,
    autoComplete: "off",
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: false,
    placeholder
  };

  const matchedFavourite = useMemo(() => {
    const queryWithSelections = {
      searchQuery: searchValue,
      selections: selectionsWithoutLabel,
      filters: {
        location: searchLocation
      }
    };
    return favourites?.find(favourite => {
      const decodedFavorite = pick(
        getURLGridQueryParams(favourite.content.trim()),
        ["searchQuery", "selections", "filters"]
      );
      return isEqual(decodedFavorite, queryWithSelections);
    });
  }, [searchValue, selectionsWithoutLabel, searchLocation, favourites]);

  const searchContainerClasses = clsx(
    "search__container",
    staticSearchBar
      ? "search__container--static"
      : "search__container--global-search-bar",
    errorMsg ? "error" : false
  );

  useEffect(() => {
    setValue(searchValue);
  }, [searchValue, gridConfig?.query_builder]);

  return (
    <>
      <div className={`flex flex-column ${className}`}>
        <CSSTransition
          in={inProp}
          timeout={0}
          classNames="search-bar-transition"
        >
          <div id={INPUT_CONTAINER_ID} className={searchContainerClasses}>
            <div
              className={`search__input-container search__input-container--${
                isActive ? "active" : "inactive"
              }`}
              data-testid="global_search_input_container"
            >
              {gridConfig?.query_builder ? (
                <QueryBuilderContainer
                  containerId={INPUT_CONTAINER_ID}
                  searchValue={value}
                  searchHistory={searchHistory}
                  onInputChange={handleQueryBuilderInputChange}
                  onSearch={handleQueryBuilderSearch}
                  onFocus={handleQueryBuilderFocus}
                  onBlur={handleBlur}
                />
              ) : (
                <SearchAutosuggest
                  inputProps={inputProps}
                  searchValue={value}
                  searchHistory={searchHistory}
                  onChange={handleChange}
                  showDatepicker={showDatepicker}
                />
              )}
            </div>
            <GlobalSearchButtons
              isLoading={isLoading}
              isActive={isActive || staticSearchBar}
              isSearchQuery={isSearchQuery}
              isSearchValueWithinLimit={isSearchValueWithinLimit}
              matchedFavourite={matchedFavourite}
              onClick={handleButtonClick}
              isLayoutStatic={staticSearchBar}
            >
              <RenderWhen
                condition={staticSearchBar && getShowNtiLink(transformedQuery)}
              >
                <div className="m-right-4">
                  <NtiLink fileName={getNtiValueFromQuery(transformedQuery)} />
                </div>
              </RenderWhen>
              <RenderWhen condition={!!errorMsg && !staticSearchBar}>
                <ErrorIcon message={errorMsg ?? ""} />
              </RenderWhen>
            </GlobalSearchButtons>
          </div>
        </CSSTransition>
        <RenderWhen condition={!!errorMsg && staticSearchBar}>
          <div className="m-top-2 icon--red flex flex-gap-1 align-center">
            <Icon name="error" />
            <span style={{ lineHeight: "1" }}>{errorMsg}</span>
          </div>
        </RenderWhen>
      </div>
      {isDatePicker && (
        <DatePickerModal
          show={isDatePicker}
          onClose={setIsDatePicker}
          onApply={onApplyDatePicker}
        />
      )}
      <RenderWhen condition={modalOpened}>
        <SearchModals
          name={modalName ?? ""}
          favoriteQuery={selectedFavourite}
          searchValue={{
            q: searchValue,
            selections,
            location: searchLocation
          }}
          onBulkSearch={onBulkSearch}
          onSearch={onSearch}
          onHistoryAndFavoritesSearch={onHistoryAndFavoritesSearch}
          onSuggestionSearch={onSuggestionSearch}
          onClose={closeModal}
        />
      </RenderWhen>
    </>
  );
};

export default SearchInput;
